import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import BlockRenderer from "@/components/cms/BlockRenderer";

export default function CmsPage({ slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const navigate = useNavigate();
  const [page, setPage] = useState(undefined);

  useEffect(() => {
    let active = true;
    setPage(undefined);
    api.get(`/public/pages/${slug}`)
      .then(({ data }) => { if (active) setPage(data); })
      .catch(async () => {
        // Redirection 301 automatique si l'URL a changé
        try {
          const { data } = await api.get(`/public/cms/redirect?slug=${encodeURIComponent(slug)}`);
          if (active && data.to) { navigate(`/p/${data.to}`, { replace: true }); return; }
        } catch (e) { void e; }
        if (active) setPage(null);
      });
    return () => { active = false; };
  }, [slug, navigate]);

  const canonical = page?.canonical && page.canonical.startsWith("/") ? page.canonical : `/p/${slug}`;
  useSeo({
    title: page?.title ? `${page.title} — PROXIreduc-28` : "PROXIreduc-28",
    description: page?.meta_description || "",
    canonicalPath: canonical,
  });

  // Balise robots (index/noindex) pilotée par la page
  useEffect(() => {
    const existing = document.querySelector('meta[name="robots"][data-cms="1"]');
    if (page?.noindex) {
      let tag = existing;
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "robots"); tag.setAttribute("data-cms", "1"); document.head.appendChild(tag); }
      tag.setAttribute("content", "noindex,nofollow");
    } else if (existing) {
      existing.remove();
    }
    return () => { const t = document.querySelector('meta[name="robots"][data-cms="1"]'); if (t) t.remove(); };
  }, [page]);

  if (page === undefined) {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-center text-slate-400" data-testid="cms-page-loading">Chargement…</div>;
  }
  if (page === null) {
    return <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="cms-page-notfound">
      <h1 className="font-heading text-2xl font-extrabold text-slate-900">Page introuvable</h1>
      <p className="mt-2 text-slate-500">Cette page n'existe pas ou n'est pas encore publiée.</p>
    </div>;
  }

  const blocks = page.blocks || [];
  const sectionBased = blocks.length > 0 && blocks.every((b) => b?.type === "section");

  if (sectionBased) {
    return (
      <article data-testid="cms-page">
        {blocks.map((b) => (
          <div key={b.id} data-testid={`cms-block-${b.type}`}><BlockRenderer block={b} /></div>
        ))}
      </article>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="cms-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6" data-testid="cms-page-title">{page.title}</h1>
      <div className="space-y-6">
        {blocks.map((b) => (
          <div key={b.id} data-testid={`cms-block-${b.type}`}>
            <BlockRenderer block={b} />
          </div>
        ))}
      </div>
    </article>
  );
}
